// ABOUTME: Dashboard builder/edit page. Lays out an ordered list of grey page sections plus an optional
// ABOUTME: trailing tab body; cells reference saved reports. Drag reorders sections, rows, divided halves, columns.
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    pointerWithin,
    TouchSensor,
    useDroppable,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import produce from "immer";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "../../theme/text/Text";
import { tokens } from "../../../theme";
import { reportBuilderAddColumn, reportBuilderAddRow, reportBuilderAddSection, reportBuilderAddTab, reportBuilderAddTabBody, reportBuilderDivideSection, reportBuilderInitialDashboard, reportBuilderRemoveColumn, reportBuilderRemoveDividedSection, reportBuilderRemoveRow, reportBuilderRemoveSection, reportBuilderRemoveTab, reportBuilderRemoveTabBody, reportBuilderRenameTab, reportBuilderResizeColumn, reportBuilderResizeDividedSection, reportBuilderSetPrimary } from "./builder/baseconfig";
import { ReportBuilderSection } from "./builder/components/ReportBuilderSection";
import { RhDragPreview } from "./builder/components/RhDragPreview";
import { RhReportPicker } from "./builder/components/RhReportPicker";
import cx from 'classnames'
import { useGetBusinessIntelligenceReportsQuery, useGetBiDashboardByIdQuery, useCreateBiDashboardMutation, useUpdateBiDashboardMutation, useDeleteBiDashboardMutation } from "../../../newapi/global/businessIntelligence";
import { dashboardCellKinds, dashboardPivotVariants } from "./builder/dashboardEnums";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ENV_TYPES } from "../Sidebar";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import Shbutton from "../../theme/buttons/Shbutton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MConfirmationWithValue from "../Modals/MConfirmationWithValue";
import MConfirmDelete from "../Modals/MConfirmDelete";
import { toast } from "react-toastify";

// The sentinel container key for the page-level sections (vs. a tab id inside the tab body).
const PAGE_SECTIONS = "sections";

// True if any cell anywhere in the subtree holds a full pivot — a full pivot is exclusive to its tab.
const containsFullPivot = (node) => {
    if (!node || typeof node !== "object") return false;
    if (node.type === "column") return node.chart?.kind === dashboardCellKinds.PIVOT && node.chart?.variant === dashboardPivotVariants.FULL;
    return Array.isArray(node.children) && node.children.some(containsFullPivot);
};

// "Drop a section here" target for an EMPTY page/tab (no sibling section to land next to). This MUST be its own
// component: a useDroppable only registers with the DndContext it renders INSIDE. Called up in ReportHub — which
// is the component that renders <DndContext> — it would attach to no context, get a null rect, and never become a
// collision candidate (the bug behind every "can't drop into empty sections" report).
const SectionDropZone = ({ id, containerKey, sx, children }) => {
    const { setNodeRef } = useDroppable({ id, data: { type: "sectionContainer", containerKey } });
    return <Box ref={setNodeRef} sx={sx}>{children}</Box>;
};

const ReportHub = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const {t} = useTranslation();
    const [dashboardConfig, setDashboardConfig] = useState({
        name: "",
        user:{
            id:-1,
            name:"Systeem"
        },
        primary:false,
        type:"fulfilment_financial", //this could also be added later
        dateRange:{
            startDate:"22-05-2025",
            endDate:"22-12-2025",
        },
        filters: [
            // use the rporthuh options.js filters for these, you may ignore this
        ],
    });
    // The layout: an ordered list of page-level grey `sections`, plus an optional trailing `tabBody`
    // (null until added). See builder/baseconfig.js for the node shapes — every node carries a stable id.
    const [dashboard, setDashboard] = useState(reportBuilderInitialDashboard);

    const {data,isLoading,isFetching,isError} = useGetBusinessIntelligenceReportsQuery();
    const [reportData,setReportData] = useState(null);

    useEffect(() => {
        if(!isLoading && !isFetching && data) {
            if(data.data) {
                const groupedReports = data.data.reduce((acc, report) => {
                    const type = report.details.type || 'orders'; // default to 'orders' if missing
                    if (!acc[type]) acc[type] = [];

                    acc[type].push(report);
                    return acc;
                }, {});
                setReportData(groupedReports);
            }
        }
    },[isLoading,isFetching,data])

    // ----------------------
    // Persistence — the builder is the edit page. Load an existing dashboard's stored JSON config (layout +
    // cell references only, no report data); save creates a new dashboard or updates the one at :id.
    // ----------------------
    const { id } = useParams();
    const navigate = useNavigate();
    // Both environments run the same hub, only under their own route prefix.
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const envPrefix = selectedEnvironmentType === ENV_TYPES[200] ? "/relation" : "/warehouse";
    const { data: dashboardData } = useGetBiDashboardByIdQuery(id, { skip: !id });
    const [createDashboard, { isLoading: creatingDashboard }] = useCreateBiDashboardMutation();
    const [updateDashboard, { isLoading: updatingDashboard }] = useUpdateBiDashboardMutation();
    const [deleteDashboard, { isLoading: deletingDashboard }] = useDeleteBiDashboardMutation();
    const hydratedRef = useRef(false);
    const [nameError, setNameError] = useState(false);

    // Hydrate once so a background refetch can never clobber in-progress edits.
    useEffect(() => {
        if (hydratedRef.current) return;
        const saved = dashboardData?.data;
        if (!saved?.config) return;
        const { dashboard: savedDashboard, ...meta } = saved.config;
        if (savedDashboard) setDashboard(savedDashboard);
        // `primary` is PER-USER: the saved config.primary is the last saver's flag, so reflect THIS user's
        // own primary state from the response instead, otherwise saving would adopt someone else's default.
        setDashboardConfig({ ...meta, name: saved.name ?? meta.name, primary: !!saved.isPrimary });
        hydratedRef.current = true;
    }, [dashboardData]);

    const handleSaveDashboard = () => {
        if (creatingDashboard || updatingDashboard) return;
        if (!dashboardConfig.name || dashboardConfig.name.trim().length === 0) {
            setNameError(true);
            toast.error(t("name_not_filled_in"), {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }
        const payload = { name: dashboardConfig.name, config: { ...dashboardConfig, dashboard } };
        if (id) {
            updateDashboard({ id, ...payload }).unwrap()
                .then((res) => { showToastMessagePostRequest(t, res); navigate(`${envPrefix}/stockhubbi/dashboard/${id}`); })
                .catch((err) => { console.error(err); showToastMessageRequestError(t("create_failed"), t, err); });
        } else {
            createDashboard(payload).unwrap()
                .then((res) => {
                    showToastMessagePostRequest(t, res);
                    const newId = res?.data?.id;
                    if (newId) navigate(`${envPrefix}/stockhubbi/dashboard/${newId}`);
                })
                .catch((err) => { console.error(err); showToastMessageRequestError(t("create_failed"), t, err); });
        }
    };

    // Only available when editing an existing dashboard (id present). Deletes it and leaves the builder.
    const [deleteOpen, setDeleteOpen] = useState(false);

    const handleDeleteDashboard = () => {
        if (!id || deletingDashboard) return;
        deleteDashboard(id).unwrap()
            .then((res) => { showToastMessagePostRequest(t, res); navigate(`${envPrefix}/dashboard`); })
            .catch((err) => { console.error(err); showToastMessageRequestError(t("delete_failed"), t, err); });
    };

    // Save As — fork the current (possibly tweaked) config into a BRAND-NEW dashboard under a new name, leaving
    // the original untouched. Always uses the create path, never updates the dashboard at :id.
    const [saveAsOpen, setSaveAsOpen] = useState(false);
    const [saveAsName, setSaveAsName] = useState("");

    const handleOpenSaveAs = () => {
        setSaveAsName(dashboardConfig.name ? `${dashboardConfig.name} copy` : "");
        setSaveAsOpen(true);
    };

    const handleSaveAs = (rawName) => {
        if (creatingDashboard) return;
        const name = (rawName || "").trim();
        if (!name) {
            toast.error(t("name_not_filled_in"), {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }
        // A fork doesn't inherit the original's primary flag (primary is per-user; the copy starts non-primary).
        const payload = { name, config: { ...dashboardConfig, name, primary: false, dashboard } };
        createDashboard(payload).unwrap()
            .then((res) => {
                showToastMessagePostRequest(t, res);
                const newId = res?.data?.id;
                if (newId) navigate(`${envPrefix}/stockhubbi/dashboard/${newId}`);
            })
            .catch((err) => { console.error(err); showToastMessageRequestError(t("create_failed"), t, err); });
    };

    const [pickerColumnId, setPickerColumnId] = useState(null);
    const [pickerAllowFull, setPickerAllowFull] = useState(false);

    // Open the picker for a column, remembering whether this cell may hold a FULL pivot (full-width body only).
    const handleOpenReportPicker = (columnId, allowFull) => {
        setPickerColumnId(columnId);
        setPickerAllowFull(!!allowFull);
    };

    // ----------------------------------------------------------------------------------------------------
    // HOW A CELL REFERENCES A REPORT (the bit that keeps getting lost):
    //   A dashboard is ONLY a layout (boxes) saved as JSON. Each box is a `column` with a `chart` field that
    //   starts empty `{}`. Picking a report in RhReportPicker sets that `chart` to a *reference*, e.g.:
    //     chart: { reportId, kind: "chart" }                          // chart cell
    //     chart: { reportId, kind: "pivot", variant: "top", n: 10 }   // bounded pivot cell (top/bottom/movers)
    //     chart: { reportId, kind: "pivot", variant: "full" }         // full pivot (rendered live)
    //   `reportId` is the id of a report you created + saved SEPARATELY in the report builder. The dashboard
    //   NEVER creates a report — it only points at an existing one. Saving the dashboard (handleSaveDashboard)
    //   persists ONLY this layout JSON (the references + variant/n), never any report data.
    // ----------------------------------------------------------------------------------------------------
    // Set (or clear, with {}) the linked report on a column, located by its unique id anywhere in the tree.
    const handleSetColumnChart = (columnId, chart) => {
        setDashboard(prev => produce(prev, draft => {
            const visit = (node) => {
                if (!node || typeof node !== "object") return;
                if (node.type === "column" && node.id === columnId) { node.chart = chart; return; }
                if (Array.isArray(node.children)) node.children.forEach(visit);
            };
            draft.sections?.forEach(visit);
            Object.values(draft.tabBody?.tabs || {}).forEach(tab => tab.children?.forEach(visit));
        }));
    };

    // ----------------------
    // Container helpers — a "container key" is either PAGE_SECTIONS (page-level list) or a tab id (inside the
    // tab body). Both resolve to an array of sections, so every section op is written once.
    // ----------------------
    const getContainerDraft = (draft, containerKey) =>
        containerKey === PAGE_SECTIONS ? draft.sections : draft.tabBody?.tabs?.[containerKey]?.children;

    const setContainerDraft = (draft, containerKey, sections) => {
        if (containerKey === PAGE_SECTIONS) draft.sections = sections;
        else if (draft.tabBody?.tabs?.[containerKey]) draft.tabBody.tabs[containerKey].children = sections;
    };

    // Replace the whole sections array of a container (add/remove/reorder a section).
    const updateContainer = (containerKey, updater) => {
        setDashboard(prev => produce(prev, draft => {
            const sections = getContainerDraft(draft, containerKey);
            if (sections) setContainerDraft(draft, containerKey, updater(sections));
        }));
    };

    // Replace a single section within a container (rows/columns/divide ops).
    const updateSection = (containerKey, sectionIndex, updater) => {
        setDashboard(prev => produce(prev, draft => {
            const sections = getContainerDraft(draft, containerKey);
            if (sections && sections[sectionIndex]) sections[sectionIndex] = updater(sections[sectionIndex]);
        }));
    };

    // ---- Section-level handlers (container-keyed; shared by page sections and tab sections) ----
    const handleAddSection = (containerKey) => updateContainer(containerKey, (sections) => reportBuilderAddSection(sections));
    // Insert a new section directly ABOVE the tab's full pivot (which must stay the last section). Drives the
    // add-section button rendered just above a pivot, since the normal bottom button can't sit below it.
    const handleAddSectionAbovePivot = (containerKey) => updateContainer(containerKey, (sections) => {
        const next = reportBuilderAddSection(sections);
        const pivotIdx = next.findIndex(containsFullPivot);
        if (pivotIdx >= 0 && pivotIdx !== next.length - 1) next.push(next.splice(pivotIdx, 1)[0]);
        return next;
    });
    const handleRemoveSection = (containerKey, sectionIndex) => updateContainer(containerKey, (sections) => reportBuilderRemoveSection(sections, sectionIndex));
    const handleDivideSection = (containerKey, sectionIndex) => updateSection(containerKey, sectionIndex, reportBuilderDivideSection);
    const handleRemoveDividedSection = (containerKey, sectionIndex, dividerIndex) => updateSection(containerKey, sectionIndex, (s) => reportBuilderRemoveDividedSection(s, dividerIndex));
    const handleResizeDividedSection = (containerKey, sectionIndex, dividerIndex, newWidth) => updateSection(containerKey, sectionIndex, (s) => reportBuilderResizeDividedSection(s, dividerIndex, newWidth));

    // ---- Row/column handlers ----
    const handleAddRow = (containerKey, sectionIndex, dividerIndex = null) => updateSection(containerKey, sectionIndex, (s) => reportBuilderAddRow(s, dividerIndex));
    const handleRemoveRow = (containerKey, sectionIndex, rowIndex, dividerIndex = null) => updateSection(containerKey, sectionIndex, (s) => reportBuilderRemoveRow(s, rowIndex, dividerIndex));
    const handleAddColumn = (containerKey, sectionIndex, rowIndex, dividerIndex = null) => updateSection(containerKey, sectionIndex, (s) => reportBuilderAddColumn(s, rowIndex, dividerIndex));
    const handleRemoveColumn = (containerKey, sectionIndex, rowIndex, colIndex, dividerIndex = null) => updateSection(containerKey, sectionIndex, (s) => reportBuilderRemoveColumn(s, rowIndex, colIndex, dividerIndex));
    const handleResizeColumn = (containerKey, sectionIndex, rowIndex, colIndex, newWidth, dividerIndex = null) => updateSection(containerKey, sectionIndex, (s) => reportBuilderResizeColumn(s, rowIndex, colIndex, newWidth, dividerIndex));

    // ---- Tab body / tabs ----
    const handleAddTabBody = () => setDashboard((prev) => reportBuilderAddTabBody(prev, t("new_tab")));
    const handleRemoveTabBody = () => setDashboard((prev) => reportBuilderRemoveTabBody(prev));
    const handleAddTab = () => setDashboard((prev) => reportBuilderAddTab(prev, t("new_tab")));
    const handleRemoveTab = (tabKey) => setDashboard((prev) => reportBuilderRemoveTab(prev, tabKey, prev.tabBody?.activeTabKey));
    const handleTabNameChange = (tabKey, value) => setDashboard((prev) => reportBuilderRenameTab(prev, tabKey, value));
    const setActiveTab = (tabKey) => setDashboard((prev) => (prev.tabBody ? { ...prev, tabBody: { ...prev.tabBody, activeTabKey: tabKey } } : prev));

    const handleSetPrimary = () => setDashboardConfig((prev) => reportBuilderSetPrimary(prev));

    // ----------------------
    // Drag & drop. One DndContext; four sortable types discriminated by `active.data.current.type`:
    //   column, row and section move freely anywhere in the tree (across page <-> tab); dividedSection
    //   (a half) only swaps within its own section. Moves are applied live in handleDragOver.
    //   FULL PIVOTS ARE LOCKED: a section/row holding one is never draggable, and nothing may be dropped
    //   into a container that holds one (it owns its whole tab — nothing goes below or beside it).
    // ----------------------
    const sensors = useSensors(
        useSensor(PointerSensor, {
            // A small drag threshold so a click on a cell's buttons (pick/change/clear) still registers
            // as a click instead of immediately starting a column drag.
            activationConstraint: { distance: 5 },
        }),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const [activeItem, setActiveItem] = useState(null);

    // Match the dragged type to the droppables it can land on, so nested sortables don't fight. SECTIONS use
    // POINTER collision (is the cursor over a section or an empty-zone placeholder?) — a section can be far taller
    // than the drag distance available above it, so closestCenter can never bring its center up to a small
    // empty-page placeholder; it just keeps colliding with itself. Everything else stays on closestCenter.
    const collisionDetectionStrategy = (args) => {
        const activeType = args.active?.data?.current?.type;
        const allowed = (c) => {
            const t = c.data?.current?.type;
            if (activeType === "column") return t === "column" || c.data?.current?.rowPath != null;
            if (activeType === "row") return t === "row" || t === "rowContainer";
            if (activeType === "section") return t === "section" || t === "sectionContainer";
            return t === activeType;
        };
        const filtered = args.droppableContainers.filter(allowed);
        if (activeType === "section") {
            const pointerHits = pointerWithin({ ...args, droppableContainers: filtered });
            if (pointerHits.length > 0) return pointerHits;
            // Keyboard sensor has no pointer -> fall back to center-based so keyboard reordering still resolves.
            // With a pointer present but over nothing we return no target, so a just-moved section is never
            // yanked back to a far sibling (no ping-pong).
            return args.pointerCoordinates ? [] : closestCenter({ ...args, droppableContainers: filtered });
        }
        return closestCenter({ ...args, droppableContainers: filtered });
    };

    // Find a node by id anywhere in the tree (page sections + every tab); returns the array that holds it
    // and its index, so any draggable can be relocated uniformly regardless of how deep it sits.
    const locateById = (root, id) => {
        let res = null;
        const walk = (arr) => {
            if (res || !Array.isArray(arr)) return;
            for (let i = 0; i < arr.length; i++) {
                const node = arr[i];
                if (node?.id === id) { res = { parent: arr, index: i }; return; }
                if (Array.isArray(node?.children)) walk(node.children);
            }
        };
        walk(root.sections);
        Object.values(root.tabBody?.tabs || {}).forEach(tab => walk(tab.children));
        return res;
    };

    // Resolve a dotted path (numeric parts are array indices; everything else, incl. uuid keys, is an object key).
    const getRowByPath = (path, root = dashboard) => {
        let current = root;
        for (const part of path.split('.')) {
            if (current == null) return null;
            current = /^\d+$/.test(part) ? current[parseInt(part, 10)] : current[part];
        }
        return current;
    };

    const handleDragStart = (event) => {
        const { active } = event;
        const type = active.data.current?.type;
        // Snapshot the dragged node so the overlay shows the real thing being moved (dnd-kit sizes it for us).
        const loc = locateById(dashboard, active.id);
        setActiveItem({ type, node: loc ? loc.parent[loc.index] : null });
    };

    // True when the dragged item's center has passed the over item's center along the relevant axis.
    const draggedPastCenter = (active, over, horizontal) => {
        const a = active.rect.current?.translated;
        const r = over.rect;
        if (!a || !r) return false;
        return horizontal
            ? (a.left + a.width / 2) > (r.left + r.width / 2)
            : (a.top + a.height / 2) > (r.top + r.height / 2);
    };

    // Resolve where the dragged item wants to go from what it's over: the destination array + an index.
    // Covers a same-type sibling, a row's rowPath (column), or an empty-container drop zone.
    const resolveDest = (draft, type, o, over, active) => {
        if (o?.type === type) {
            const dst = locateById(draft, over.id);
            if (!dst) return null;
            const horizontal = type === "column" || type === "dividedSection";
            return { parent: dst.parent, index: dst.index + (draggedPastCenter(active, over, horizontal) ? 1 : 0) };
        }
        if (type === "column" && o?.rowPath != null) {
            const overRow = getRowByPath(o.rowPath, draft);
            return overRow ? { parent: overRow.children, index: overRow.children.length } : null;
        }
        if (type === "row" && o?.type === "rowContainer") {
            const section = getContainerDraft(draft, o.containerKey)?.[o.sectionIndex];
            const rows = section && (o.dividerIndex != null ? section.children[o.dividerIndex]?.children : section.children);
            return rows ? { parent: rows, index: rows.length } : null;
        }
        if (type === "section" && o?.type === "sectionContainer") {
            const sections = getContainerDraft(draft, o.containerKey);
            return sections ? { parent: sections, index: sections.length } : null;
        }
        return null;
    };

    // A full pivot owns everything below it, so keep its section pinned to the END of its container after any
    // move (sections may stack above it, never below).
    const keepFullPivotLast = (sections) => {
        if (!Array.isArray(sections)) return;
        const idx = sections.findIndex(containsFullPivot);
        if (idx >= 0 && idx !== sections.length - 1) sections.push(sections.splice(idx, 1)[0]);
    };

    // CROSS-container moves only — relocate the dragged node into a different parent as it hovers there. Same-
    // container reordering is intentionally left to SortableContext's animation and committed on drag end, so
    // big differently-sized items (sections/rows) don't oscillate from being spliced on every hover.
    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const type = active.data.current?.type;
        const o = over.data?.current;

        setDashboard(prev => produce(prev, draft => {
            const src = locateById(draft, active.id);
            if (!src) return;
            if (containsFullPivot(src.parent[src.index])) return; // a full pivot never leaves its tab
            if (type === "dividedSection") return; // halves only swap within their section (handled on drag end)

            const dest = resolveDest(draft, type, o, over, active);
            if (!dest || dest.parent === src.parent) return; // same container -> leave it to drag end
            const pivotIdx = dest.parent.findIndex(containsFullPivot);
            if (pivotIdx >= 0) {
                if (type !== "section") return; // rows/columns can never enter a full-pivot section/row
                dest.index = Math.min(dest.index, pivotIdx); // a section only ever lands ABOVE the pivot
            }

            const [moved] = src.parent.splice(src.index, 1);
            dest.parent.splice(Math.max(0, Math.min(dest.index, dest.parent.length)), 0, moved);
            keepFullPivotLast(dest.parent);
        }));
    };

    // Commit a SAME-container reorder (cross-container already happened live in handleDragOver).
    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveItem(null);
        if (!over || active.id === over.id) return;
        const type = active.data.current?.type;
        const o = over.data?.current;

        if (o?.type !== type) return; // only reorder next to a same-type sibling
        setDashboard(prev => produce(prev, draft => {
            const src = locateById(draft, active.id);
            const dst = locateById(draft, over.id);
            if (!src || !dst || src.parent !== dst.parent || src.index === dst.index) return;
            if (containsFullPivot(src.parent[src.index])) return;
            // Equivalent to arrayMove(parent, src.index, dst.index): remove, then insert at the target index.
            const [moved] = src.parent.splice(src.index, 1);
            src.parent.splice(dst.index, 0, moved);
            keepFullPivotLast(src.parent); // a section dropped past the pivot snaps back above it
        }));
    };

    // Section props shared by both containers (page sections + tab sections).
    const sectionHandlers = {
        handleRemoveSection,
        handleAddRow,
        handleRemoveRow,
        handleAddColumn,
        handleRemoveColumn,
        handleResizeColumn,
        handleDivideSection,
        handleRemoveDividedSection,
        handleResizeDividedSection,
        onOpenReportPicker: handleOpenReportPicker,
        handleSetColumnChart,
    };

    const tabBody = dashboard.tabBody;
    const tabEntries = Object.entries(tabBody?.tabs || {});
    const activeTabKey = tabBody && (tabBody.tabs?.[tabBody.activeTabKey] ? tabBody.activeTabKey : tabEntries[0]?.[0]);
    const activeTab = activeTabKey ? tabBody.tabs[activeTabKey] : null;
    const tabAllowsAddSection = !(activeTab?.children || []).some(containsFullPivot);

    // Whether the page / active tab currently hold no sections. Both then render a SectionDropZone (whose
    // useDroppable registers inside the DndContext) so a dragged section has somewhere to land.
    const pageEmpty = (dashboard.sections || []).length === 0;
    const tabEmpty = !!activeTabKey && (activeTab?.children || []).length === 0;
    // True only while a section is mid-drag — used to tint / size the empty-zone targets.
    const draggingSection = activeItem?.type === "section";

    return (

        <Box
            sx={{
                background:colors.bg['tertiary'],
                width:1,
                paddingTop:10, //5 from topbar, + 5 spacing to topbar
                // Flex column so the tab body can claim whatever height is left below the header and sections.
                minHeight:"100vh",
                display:"flex",
                flexDirection:"column",
            }}
        >
            <Box
                sx={{
                    px:4,
                    display:'flex',
                    justifyContent:'space-between',
                    alignItems:'flex-start',
                }}
            >
                {/* Left side */}
                <Box>
                    {/* Title — editable, required name (same styling as an individual report's name field) */}
                    <Box
                        className={cx(undefined, {
                            'is-error':nameError,
                        })}
                        sx={{
                            display:'flex',
                            alignItems:"center",
                            px:0.5,
                            height:32,
                            borderRadius:12,
                            minWidth:450,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[200],
                            "@media screen and (max-width: 768px)" : {
                                minWidth:"unset",
                                width:1,
                            },
                            "&.is-error" : {
                                background:colors.red[100],
                                borderColor:colors.red[400],
                            }
                        }}
                    >
                        <Box
                            className={cx(undefined, {
                                'is-error':nameError,
                            })}
                            sx={{
                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                height:24,
                                borderRadius:4,
                                px:2,
                                lineHeight:"24px",
                                "&.is-error": {
                                    background:colors.red[400],
                                }
                            }}
                        >
                            <Text
                                color={nameError && (theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['light'])}
                            >
                                {t("name")}
                            </Text>
                        </Box>
                        <InputBase
                            value={dashboardConfig.name}
                            onChange={(e) => {
                                setNameError(false);
                                setDashboardConfig(prev => ({
                                    ...prev,
                                    name:e.target.value,
                                }))
                            }}
                            name='dashboard_name'
                            placeholder={t("name")}
                            sx={{
                                flex:1,
                                height:1,
                                width:'auto',
                                fontSize:14,
                                px:2,
                                fontWeight:1000,
                                fontFamily:"Roboto, sans-serif",
                                color:colors.txt["primary"],
                            }}
                        />
                        <Box>
                            {dashboardConfig.primary ?
                                <ButtonBase
                                    onClick={() => setDashboardConfig((prev) => ({ ...prev, primary: false }))}
                                    sx={{
                                        height:24,
                                        fontSize:12,
                                        fontWeight:700,
                                        fontFamily:"Roboto, sans-serif",
                                        borderRadius:5,
                                        background:colors.purple[400],
                                        color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        px:1,
                                    }}
                                >
                                    {t('primary')}
                                </ButtonBase>
                                :
                                <ButtonBase
                                    onClick={() => handleSetPrimary()}
                                    sx={{
                                        height:24,
                                        fontSize:12,
                                        fontWeight:700,
                                        fontFamily:"Roboto, sans-serif",
                                        borderRadius:5,
                                        background:colors.purple[100],
                                        color:colors.purple[400],
                                        px:1,
                                    }}
                                >
                                    {t('make_primary')}
                                </ButtonBase>
                            }
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {id &&
                        <ButtonBase
                            onClick={() => setDeleteOpen(true)}
                            disabled={deletingDashboard}
                            sx={{
                                height: 40,
                                px: 2,
                                gap: 1,
                                borderRadius: 5,
                                fontSize: 14,
                                fontWeight: 700,
                                fontFamily: "Roboto, sans-serif",
                                background: colors.red[100],
                                color: colors.red[400],
                                "&:hover": { background: colors.red[200] },
                            }}
                        >
                            <DeleteOutlinedIcon fontSize="small" />
                            {t("delete_dashboard")}
                        </ButtonBase>
                    }
                    {id &&
                        <Shbutton
                            color={theme.palette.mode === "dark" ? "primaryGrey" : 'grey'}
                            variant="contained"
                            onClick={handleOpenSaveAs}
                            startIcon={<SaveAsOutlinedIcon />}
                        >
                            {t("save_as")}
                        </Shbutton>
                    }
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                        variant="contained"
                        onClick={handleSaveDashboard}
                        isLoading={creatingDashboard || updatingDashboard}
                        startIcon={<SaveOutlinedIcon />}
                    >
                        {t("save")}
                    </Shbutton>
                </Box>
            </Box>

            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionStrategy}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                // Re-measure droppables continuously so empty-container drop zones that only appear/enable mid-drag
                // (e.g. the page emptying as you drag its last section out) still register a hitbox.
                measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            >
                {/* PAGE SECTIONS — ordered, draggable grey sections */}
                <Box sx={{ mt: 4, px: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                    {pageEmpty ? (
                        // EMPTY page: a SectionDropZone (its useDroppable registers inside the DndContext) that grows
                        // large while a section is dragged so the cursor easily lands in it. The add-section button
                        // lives inside it, so there's no extra whitespace at rest.
                        <SectionDropZone
                            id="sectionzone-page"
                            containerKey={PAGE_SECTIONS}
                            sx={{
                                width: 1,
                                // minHeight: draggingSection ? 200 : undefined,
                                borderRadius: 4,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                // p: draggingSection ? 1.5 : 0,
                                // background: draggingSection ? (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]) : "transparent",
                                transition: "min-height 120ms ease",
                            }}
                        >
                            <ButtonBase
                                className="add-section-btn"
                                onClick={() => handleAddSection(PAGE_SECTIONS)}
                                sx={{
                                    width: 1,
                                    height: 32,
                                    borderRadius: 4,
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                }}
                            >
                                {t("add_section")}
                            </ButtonBase>
                        </SectionDropZone>
                    ) : (
                        <>
                            <SortableContext items={(dashboard.sections || []).map(s => s.id)} strategy={verticalListSortingStrategy}>
                                {(dashboard.sections || []).map((section, si) => (
                                    <ReportBuilderSection
                                        key={section.id}
                                        containerKey={PAGE_SECTIONS}
                                        section={section}
                                        si={si}
                                        sectionPath={`${PAGE_SECTIONS}.${si}`}
                                        tabSectionCount={dashboard.sections.length}
                                        {...sectionHandlers}
                                    />
                                ))}
                            </SortableContext>
                            <ButtonBase
                                className="add-section-btn"
                                onClick={() => handleAddSection(PAGE_SECTIONS)}
                                sx={{
                                    width: 1,
                                    height: 32,
                                    borderRadius: 4,
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                }}
                            >
                                {t("add_section")}
                            </ButtonBase>
                        </>
                    )}
                </Box>

                {/* TAB BODY — optional, always pinned as the last main element of the page */}
                {tabBody ? (
                    <>
                        <Box sx={{ mt: 5, px: 4, display: "flex", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                {tabEntries.map(([tabKey, tab]) => {
                                    const isActive = tabKey === activeTabKey;
                                    return (
                                        <Box key={tabKey} sx={{ display: "flex", alignItems: "center" }}>
                                            <ButtonBase
                                                className={cx(undefined, { 'active': isActive })}
                                                onClick={() => { if (!isActive) setActiveTab(tabKey); }}
                                                sx={{
                                                    display:'flex',
                                                    alignItems:'center',
                                                    px:2,
                                                    height:40,
                                                    fontSize:14,
                                                    fontFamily:"Roboto,sans-serif",
                                                    color:colors.txt["secondary"],
                                                    fontWeight:500,
                                                    "&:after" : {
                                                        content: '""',
                                                        position:"absolute",
                                                        width:1,
                                                        bottom:0,
                                                        borderRadius:1,
                                                        height:"2px",
                                                    },
                                                    "&.active" : {
                                                        color: `${colors.txt["primary"]}`,
                                                        fontWeight:700,
                                                        "&:after" :{
                                                            background: `${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                                        }
                                                    },
                                                    "&:hover" : {
                                                        color: `${colors.txt["primary"]}`,
                                                        backgroundColor: "transparent",
                                                    },
                                                }}
                                            >
                                                {!isActive ?
                                                    tab.name
                                                :
                                                    <InputBase
                                                        autoFocus
                                                        value={tab.name}
                                                        onChange={(e) => handleTabNameChange(tabKey, e.target.value)}
                                                        sx={{
                                                            height:24,
                                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                            px:1.5,
                                                            borderRadius:3,
                                                        }}
                                                    />
                                                }
                                            </ButtonBase>

                                            {!isActive &&
                                                <ButtonBase
                                                    onClick={() => handleRemoveTab(tabKey)}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    ✕
                                                </ButtonBase>
                                            }
                                        </Box>
                                    );
                                })}
                                <ButtonBase
                                    onClick={handleAddTab}
                                    sx={{
                                        height:32,
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                        px:2,
                                        borderRadius:4,
                                    }}
                                >
                                    {t("add_tab")}
                                </ButtonBase>
                            </Box>

                            <ButtonBase
                                onClick={handleRemoveTabBody}
                                sx={{
                                    px:1.5,
                                    // width: 1,
                                    height: 32,
                                    borderRadius: 4,
                                    background: colors.red[100],
                                    color: colors.red[400],
                                    "&:hover": { background: colors.red[200] },
                                }}
                            >
                                {t("remove_tab_body")}
                            </ButtonBase>
                        </Box>
                        <Box
                            sx={{
                                width:1,
                                // Runs to the bottom of the viewport even when the tab holds little content.
                                flex:1,
                                background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                position:"relative",
                                zIndex:2,
                                pt:4,
                                px:4,
                                pb:4,
                                display:"flex",
                                flexDirection:"column",
                                gap:4,
                                "&:before" : {
                                    content: "''",
                                    display:"block",
                                    position:"absolute",
                                    left:0,
                                    zIndex:1,
                                    backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                    top:"0px",
                                    height:"48px",
                                    width:"24px",
                                    borderTopLeftRadius:"24px",
                                    boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                                    transition:"250ms"
                                },
                                "&:after" : {
                                    content: "''",
                                    display:"block",
                                    position:"absolute",
                                    top:0,
                                    zIndex:1,
                                    backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                                    right:0,
                                    height:"48px",
                                    width:"24px",
                                    borderTopLeftRadius:"24px",
                                    boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                                    transform:"scaleX(-1)",
                                    transition:"250ms"
                                },
                                "@media screen and (max-width: 47.9375em)" : {
                                    "&:after, &:before" : {
                                        top:"-0px"
                                    }
                                }
                            }}
                        >
                            {/* Exclude a full-pivot section from the sortable set so the strategy never previews
                                another section sliding below it — it stays pinned at the bottom. */}
                            <SortableContext items={(activeTab?.children || []).filter(s => !containsFullPivot(s)).map(s => s.id)} strategy={verticalListSortingStrategy}>
                                {(activeTab?.children || []).map((section, si) => (
                                    <Fragment key={section.id}>
                                        {/* A full pivot is pinned to the bottom, so its add-section button sits ABOVE it
                                            (the normal one below would land past the pivot). Adds a section above the pivot. */}
                                        {containsFullPivot(section) &&
                                            <ButtonBase
                                                className="add-section-btn"
                                                onClick={() => handleAddSectionAbovePivot(activeTabKey)}
                                                sx={{
                                                    width: 1,
                                                    height: 32,
                                                    borderRadius: 4,
                                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                                }}
                                            >
                                                {t("add_section")}
                                            </ButtonBase>
                                        }
                                        <ReportBuilderSection
                                            containerKey={activeTabKey}
                                            section={section}
                                            si={si}
                                            sectionPath={`tabBody.tabs.${activeTabKey}.children.${si}`}
                                            tabSectionCount={activeTab.children.length}
                                            {...sectionHandlers}
                                        />
                                    </Fragment>
                                ))}
                            </SortableContext>
                            {tabEmpty &&
                                // Empty tab: an ABSOLUTE overlay covering the whole tab body (which is position:relative)
                                // so a section dragged in from anywhere registers immediately — but, being out of flow,
                                // it takes no space and never pushes the add/remove buttons down. pointerEvents:none
                                // keeps those buttons clickable; dnd-kit collision is geometric so the drop still works.
                                <SectionDropZone
                                    id="sectionzone-tab"
                                    containerKey={activeTabKey}
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                    }}
                                />
                            }
                            {tabAllowsAddSection &&
                                <ButtonBase
                                    className="add-section-btn"
                                    onClick={() => handleAddSection(activeTabKey)}
                                    sx={{
                                        width: 1,
                                        height: 32,
                                        borderRadius: 4,
                                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                                    }}
                                >
                                    {t("add_section")}
                                </ButtonBase>
                            }
                        </Box>
                    </>
                ) : (
                    <Box sx={{ mt: 5, }}>
                        <ButtonBase
                            onClick={handleAddTabBody}
                            sx={{
                                width: 1,
                                borderTopLeftRadius:5,
                                borderTopRightRadius:5,
                                minHeight: 240,
                                gap: 1.5,
                                color: colors.txt["secondary"],
                                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[50],
                                color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                transition:"250ms",
                                "&:hover": {
                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                },
                            }}
                        >
                            <AddOutlinedIcon />
                            <Text>{t("add_tab_body")}</Text>
                        </ButtonBase>
                    </Box>
                )}

                <DragOverlay>
                    {activeItem?.node ? <RhDragPreview node={activeItem.node} /> : null}
                </DragOverlay>
            </DndContext>

            <RhReportPicker
                open={pickerColumnId != null}
                allowFullPivot={pickerAllowFull}
                onClose={() => setPickerColumnId(null)}
                onSelect={(chart) => { handleSetColumnChart(pickerColumnId, chart); setPickerColumnId(null); }}
            />

            <MConfirmationWithValue
                open={saveAsOpen}
                handleClose={() => setSaveAsOpen(false)}
                data={{
                    title: t("save_as"),
                    subtitle: t("save_as_desc"),
                    hasInput: true,
                    inputType: "text",
                    inputLabel: "name",
                    value: saveAsName,
                }}
                onChange={(val) => setSaveAsName(val)}
                confirm={(val) => handleSaveAs(val)}
            />

            <MConfirmDelete
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                confirm={handleDeleteDashboard}
                data={dashboardConfig.name}
            />
        </Box>
    );
};

export default ReportHub;
